/**
 * MML raster-proxy capability discovery (bootstrap half).
 *
 * Called once from `main.tsx` alongside `initialiseMediaDelivery`. Kept out
 * of `mmlTileAvailability.ts` so the store module renderers import contains
 * no server request (route-purity: renderers never fetch).
 */
import { setMmlTilesAvailable } from './mmlTileAvailability';

const AVAILABILITY_URL = '/api/v1/mml/tiles/availability';

/**
 * Budget for the one-shot capability probe. A healthy proxy answers this
 * endpoint in milliseconds; past this budget we abandon the probe (the store
 * stays null — see below) rather than block on a hung request forever.
 */
const AVAILABILITY_TIMEOUT_MS = 5000;

/**
 * Only an AUTHORITATIVE 200 answer from the proxy latches the store; any
 * inconclusive outcome (non-ok status, network error, timeout) leaves it
 * null. With null no longer blocking rendering (`resolveBasemapMode`),
 * basemaps then render MML optimistically and the runtime tileerror→OSM
 * fallback decides per-map — so a healthy proxy that was briefly unreachable
 * at boot recovers naturally instead of pinning the whole session to OSM.
 */
export async function initialiseMmlTileAvailability(): Promise<void> {
  try {
    const response = await fetch(AVAILABILITY_URL, {
      headers: { Accept: 'application/json' },
      signal: AbortSignal.timeout(AVAILABILITY_TIMEOUT_MS),
    });
    if (!response.ok) {
      // Transient/unexpected proxy state, not a capability answer → keep null.
      return;
    }
    const data: { available?: unknown } = await response.json();
    setMmlTilesAvailable(Boolean(data.available));
  } catch {
    // Network error or timeout — same non-authoritative treatment: keep null.
  }
}
